import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { LegalLayout } from './LegalLayout.js';
import { Mono, P, Plain, Terms } from './prose.js';

/**
 * The page the data-deletion callback promises Meta.
 *
 * Meta's contract is that the `url` we return is openable by a person, so this
 * is a page and not a JSON document: no login, no dashboard, one confirmation
 * code, and the counts read back from the record rather than restated from
 * whatever the callback believed at the time.
 */

interface RetainedNote {
  title: string;
  body: string;
}

interface DeletionStatus {
  code: string;
  status: 'received' | 'completed' | 'failed';
  requestedAt: string;
  completedAt: string | null;
  source: string;
  removed: {
    businesses: number;
    conversations: number;
    messages: number;
    profiles: number;
    optIns: number;
  };
  retained: RetainedNote[];
  error: string | null;
  contactEmail: string;
}

const STAMP = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
});

const stamp = (iso: string) => `${STAMP.format(new Date(iso))} UTC`;

const HEADLINE: Record<DeletionStatus['status'], string> = {
  received: 'The request is being carried out',
  completed: 'The deletion is done',
  failed: 'The deletion did not complete',
};

class StatusError extends Error {
  constructor(readonly status: number) {
    super(`HTTP ${status}`);
  }
}

async function fetchStatus(code: string): Promise<DeletionStatus> {
  const response = await fetch(`/api/compliance/deletion/${encodeURIComponent(code)}`, {
    headers: { accept: 'application/json' },
  });
  if (!response.ok) throw new StatusError(response.status);
  return (await response.json()) as DeletionStatus;
}

function StatusLine({ tone, children }: { tone: 'seal' | 'alert' | 'muted'; children: string }) {
  const colour =
    tone === 'seal' ? 'text-seal' : tone === 'alert' ? 'text-alert' : 'text-ink/70';
  return (
    <div className="flex items-center gap-3">
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${tone === 'seal' ? 'bg-seal' : tone === 'alert' ? 'bg-alert' : 'bg-ink/40'}`} />
      <span className={`text-sm font-medium ${colour}`}>{children}</span>
    </div>
  );
}

export function DeletionStatusPage() {
  const { code = '' } = useParams<{ code: string }>();
  const query = useQuery({
    queryKey: ['deletion-status', code],
    queryFn: () => fetchStatus(code),
    retry: (failureCount, error) =>
      error instanceof StatusError && error.status === 404 ? false : failureCount < 2,
  });

  const data = query.data;
  const notFound = query.error instanceof StatusError && query.error.status === 404;

  return (
    <LegalLayout
      label="Data deletion"
      title="Deletion status"
      effective="2026-08-05"
      standfirst={
        <>
          The record of one deletion request, readable without an account. Quote the confirmation
          code below in any correspondence about it.
        </>
      }
    >
      <div className="text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium">
        Confirmation code
      </div>
      <div className="mt-2 font-mono text-base sm:text-lg text-ink break-all">{code}</div>

      <div className="mt-8 h-px w-full bg-hairline" />

      {query.isPending ? (
        <p className="mt-8 text-sm text-ink/50">Reading the record…</p>
      ) : notFound ? (
        <div className="mt-8 space-y-4">
          <StatusLine tone="alert">No request carries that code</StatusLine>
          <P>
            Check the code against the message that gave it to you — it is <Mono>del_</Mono> followed
            by sixteen characters. If it is right and this page still says no, write to{' '}
            <Mono>privacy@legate.dev</Mono> quoting it, and we will answer within 3 working days.
          </P>
          <P>
            The instructions for making a request are on the{' '}
            <Link
              to="/data-deletion"
              className="text-accent hover:text-accent-hover underline underline-offset-4 transition-colors duration-200"
            >
              data deletion page
            </Link>
            .
          </P>
        </div>
      ) : query.isError || !data ? (
        <div className="mt-8 space-y-4">
          <StatusLine tone="alert">The status could not be read</StatusLine>
          <P>
            This is a fault on our side, not a statement about your request. Reload the page, and if
            it persists write to <Mono>privacy@legate.dev</Mono> quoting the code above.
          </P>
        </div>
      ) : (
        <div className="mt-8 space-y-8">
          <div className="space-y-3">
            <StatusLine
              tone={data.status === 'completed' ? 'seal' : data.status === 'failed' ? 'alert' : 'muted'}
            >
              {HEADLINE[data.status]}
            </StatusLine>
            <Plain>
              {data.status === 'completed'
                ? 'Everything listed below was removed and the removal was verified by reading the records back.'
                : data.status === 'failed'
                  ? 'Part of the request did not go through. It has been recorded and is being handled by a person.'
                  : 'The request has been accepted and is running.'}
            </Plain>
            {data.error ? (
              <p className="text-sm text-alert leading-relaxed">{data.error}</p>
            ) : null}
          </div>

          <Terms
            items={[
              { term: 'Requested', detail: stamp(data.requestedAt) },
              {
                term: 'Completed',
                detail: data.completedAt ? stamp(data.completedAt) : 'Not yet',
              },
              { term: 'Source', detail: data.source },
            ]}
          />

          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium">
              Removed
            </div>
            <div className="mt-4">
              <Terms
                items={[
                  {
                    term: 'Businesses affected',
                    detail: <span className="tabular">{data.removed.businesses}</span>,
                  },
                  {
                    term: 'Conversations',
                    detail: <span className="tabular">{data.removed.conversations}</span>,
                  },
                  {
                    term: 'Messages',
                    detail: <span className="tabular">{data.removed.messages}</span>,
                  },
                  {
                    term: 'Profile records',
                    detail: <span className="tabular">{data.removed.profiles}</span>,
                  },
                  {
                    term: 'WhatsApp opt-ins',
                    detail: <span className="tabular">{data.removed.optIns}</span>,
                  },
                ]}
              />
            </div>
            {data.removed.conversations === 0 &&
            data.removed.messages === 0 &&
            data.removed.optIns === 0 ? (
              <p className="mt-4 text-sm text-ink/70 leading-relaxed">
                Nothing matched. Either the records had already been deleted, or this account never
                held any — both are a finished request, not an error.
              </p>
            ) : null}
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium">
              Kept, and why
            </div>
            <div className="mt-4 space-y-5">
              {data.retained.map((note) => (
                <div key={note.title}>
                  <div className="text-sm font-medium text-ink">{note.title}</div>
                  <p className="mt-1.5 text-sm text-ink/70 leading-relaxed">{note.body}</p>
                </div>
              ))}
            </div>
          </div>

          <P>
            Anything unclear, or a record you believe should have gone and did not: write to{' '}
            <Mono>{data.contactEmail}</Mono> quoting this code. The full policy is on the{' '}
            <Link
              to="/privacy"
              className="text-accent hover:text-accent-hover underline underline-offset-4 transition-colors duration-200"
            >
              privacy page
            </Link>
            .
          </P>
        </div>
      )}
    </LegalLayout>
  );
}

export default DeletionStatusPage;
